import React, { useEffect, useState, useCallback, useRef } from "react";
import { db } from "../../firebase";
import { validateRutUY } from "../../utils/validateRutUY";
import {
  collection,
  getDocs,
  updateDoc,
  addDoc,
  doc,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";
import { useBuilding } from "../../context/BuildingContext";
import { tolerantId } from "../../utils/firestoreHelpers";
import { bidCanonico, esPermisoDenegado } from "../../utils/consultasEdificio";
import { FaPen, FaSearch, FaCheckCircle, FaMap, FaList, FaMapMarkerAlt, FaPeopleCarry, FaLink, FaChevronDown, FaEraser, FaTag, FaEnvelope, FaPhone, FaIdCard, FaBuilding } from "react-icons/fa";
import AdminModal from "../../components/AdminModal";
import ScreenHeader from "../../components/ScreenHeader";
import { useToast } from "../../components/Toast";
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from "@react-google-maps/api";
import { colors, shadows, radius, spacing, typography, transitions, cardBase, btnPrimary, btnSecondary, btnDanger, btnGhost, inputBase, pageContainer, pageTitle } from "../../utils/designTokens";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const libraries = ["places"];

const mapContainerStyle = {
  width: "100%",
  height: "500px",
  borderRadius: radius.md,
};

const defaultCenter = {
  lat: -34.9011, // Montevideo
  lng: -56.1645,
};

const SUPPLIER_TYPES = [
  "Servicios Públicos",
  "Telecomunicaciones",
  "Seguros",
  "Mantenimiento",
  "Jardinería",
  "Limpieza",
  "Seguridad",
  "Administración",
  "Construcción",
  "Otro",
];

const ProveedoresEdificio = ({ setSelected }) => {
  const { buildings, selectedBuilding: globalSelectedBuilding } = useBuilding();
  const { showToast } = useToast();

  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // "list" | "map"
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [form, setForm] = useState({
    name: "",
    type: "",
    email: "",
    telephone: "",
    address: "",
    razonSocial: "",
    rut: "",
    location: null, // { lat, lng } para geolocalización
  });

  // Estados para el modal de asociar proveedor
  const [showAssociateModal, setShowAssociateModal] = useState(false);
  const [allProviders, setAllProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState("");
  const [showToClient, setShowToClient] = useState(true);
  const [associating, setAssociating] = useState(false);
  const [showProviderDropdown, setShowProviderDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [supplierBuildingRelations, setSupplierBuildingRelations] = useState([]);
  
  // Estado para mostrar aviso de asociación automática
  const [showAutoAssociateNotice, setShowAutoAssociateNotice] = useState(false);
  const [rutError, setRutError] = useState(null);

  // Estado para modal de RUT duplicado
  const [showRutDuplicateModal, setShowRutDuplicateModal] = useState(false);
  const [duplicateRutSupplier, setDuplicateRutSupplier] = useState(null);

  // Filtros de lista
  const [filterRut, setFilterRut] = useState("");
  const [filterNombre, setFilterNombre] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [filterTipoOpen, setFilterTipoOpen] = useState(false);

  // Google Maps
  const { isLoaded: isGoogleMapsLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const mapRef = useRef(null);
  const supplierAddressInputRef = useRef(null);
  const supplierAddressAutocompleteRef = useRef(null);
  const filterTipoRef = useRef(null);
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Proveedores filtrados
  const filteredProveedores = proveedores.filter((p) => {
    const rutMatch = !filterRut || (p.rut && String(p.rut).toLowerCase().includes(filterRut.toLowerCase()));
    const nombreMatch = !filterNombre || (p.name && p.name.toLowerCase().includes(filterNombre.toLowerCase()));
    const tipoMatch = !filterTipo || (p.type && p.type === filterTipo);
    return rutMatch && nombreMatch && tipoMatch;
  });

  // Proveedores con coordenadas para el mapa (a partir de los filtrados)
  const proveedoresWithCoords = filteredProveedores.filter(p => p.location?.lat && p.location?.lng);

  const getMapCenter = () => {
    if (proveedoresWithCoords.length > 0) {
      const avgLat = proveedoresWithCoords.reduce((sum, p) => sum + p.location.lat, 0) / proveedoresWithCoords.length;
      const avgLng = proveedoresWithCoords.reduce((sum, p) => sum + p.location.lng, 0) / proveedoresWithCoords.length;
      return { lat: avgLat, lng: avgLng };
    }
    return defaultCenter;
  };

  const fetchProveedores = async () => {
    setLoading(true);
    try {
      // Relaciones proveedor-edificio SOLO del edificio elegido (reglas 0D: la
      // lectura de supplierbuilding mira su buildingId, número o string). Sin
      // edificio elegido no hacen falta: se lista el catálogo completo.
      let relations = [];
      if (globalSelectedBuilding?.buildingid) {
        const relationsSnap = await getDocs(
          query(
            collection(db, "supplierbuilding"),
            where("buildingId", "in", tolerantId(globalSelectedBuilding.buildingid))
          )
        );
        relations = relationsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
      setSupplierBuildingRelations(relations);
      
      // Cargar todos los proveedores
      const snapshot = await getDocs(collection(db, "supplier"));
      const allProviders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Si hay un edificio seleccionado en el filtro global, filtrar proveedores
      if (globalSelectedBuilding?.buildingid) {
        const providerIdsForBuilding = relations.map(r => String(r.supplierId));
        
        const filteredProviders = allProviders.filter(p => 
          providerIdsForBuilding.includes(String(p.supplierId))
        );
        setProveedores(filteredProviders);
      } else {
        setProveedores(allProviders);
      }
    } catch (error) {
      console.error("Error fetching providers:", error);
      setProveedores([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProveedores();
    fetchAllProviders();
  }, [globalSelectedBuilding]);

  useEffect(() => {
    filterProviders();
  }, [searchTerm, allProviders]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterTipoOpen && filterTipoRef.current && !filterTipoRef.current.contains(event.target)) {
        setFilterTipoOpen(false);
      }
      if (showProviderDropdown && !event.target.closest('[data-dropdown]')) {
        setShowProviderDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProviderDropdown]);

  // Google Places Autocomplete para dirección del proveedor (al dar alta desde pantalla Proveedores)
  useEffect(() => {
    let timeoutId;
    const initAutocomplete = () => {
      if (!supplierAddressInputRef.current || !window.google?.maps?.places) return;
      try {
        if (supplierAddressAutocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(supplierAddressAutocompleteRef.current);
          supplierAddressAutocompleteRef.current = null;
        }
        const autocomplete = new window.google.maps.places.Autocomplete(supplierAddressInputRef.current, {
          types: ["address"],
          componentRestrictions: { country: ["uy"] },
        });
        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (place.geometry) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            const formattedAddress = place.formatted_address || supplierAddressInputRef.current.value;
            setForm(prev => ({
              ...prev,
              address: formattedAddress,
              location: { lat, lng }
            }));
          }
        });
        supplierAddressAutocompleteRef.current = autocomplete;
      } catch (error) {
        console.error("Error inicializando autocomplete dirección:", error);
      }
    };
    if (showForm && isGoogleMapsLoaded) {
      timeoutId = setTimeout(initAutocomplete, 300);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (supplierAddressAutocompleteRef.current) {
        try {
          window.google?.maps?.event?.clearInstanceListeners?.(supplierAddressAutocompleteRef.current);
        } catch (_) {}
        supplierAddressAutocompleteRef.current = null;
      }
    };
  }, [showForm, isGoogleMapsLoaded]);

  // Geocodificar dirección cuando el formulario se abre con dirección pero sin coordenadas (ej. al editar)
  useEffect(() => {
    if (!showForm || !form.address?.trim() || !window.google?.maps) return;
    if (form.location?.lat != null && form.location?.lng != null) return;

    const geocoder = new window.google.maps.Geocoder();
    const addr = form.address.trim();
    geocoder.geocode({ address: addr + ", Uruguay" }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const loc = results[0].geometry.location;
        setForm(prev => {
          if (prev.address?.trim() !== addr) return prev;
          return { ...prev, location: { lat: loc.lat(), lng: loc.lng() } };
        });
      }
    });
  }, [showForm, form.address]);

  // Helper para calcular el próximo supplierID disponible
  const calcNextSupplierIdFromList = (list) => {
    const existing = new Set((list || []).map((s) => String(s.supplierId || s.supplierid)));
    let candidate = "1";
    // Avanza hasta encontrar el primer entero libre empezando en 1
    while (existing.has(candidate)) {
      candidate = String(parseInt(candidate, 10) + 1);
    }
    return candidate;
  };

  const handleSave = async () => {
    if (form.rut) {
      const rutValidation = validateRutUY(form.rut);
      if (!rutValidation.valid) {
        setRutError(rutValidation.message);
        return;
      }
    }
    setRutError(null);
    setSaving(true);
    try {
      // BUG-71 fix: Verificar RUT duplicado al crear Y al editar (si cambió)
      if (form.rut) {
        const rutQuery = query(
          collection(db, "supplier"),
          where("rut", "==", form.rut)
        );
        const rutSnap = await getDocs(rutQuery);
        // Excluir el proveedor actual si estamos editando
        const duplicates = rutSnap.docs.filter(d => !(editing && d.id === editing));
        if (duplicates.length > 0) {
          const existingSupplier = { id: duplicates[0].id, ...duplicates[0].data() };
          setDuplicateRutSupplier(existingSupplier);
          setShowRutDuplicateModal(true);
          setSaving(false);
          return;
        }
      }

      if (editing) {
        // Editando proveedor existente
        const payload = {
          ...form,
          updatedAt: serverTimestamp(),
        };
        if (form.location) {
          payload.location = { lat: form.location.lat, lng: form.location.lng };
        }
        const ref = doc(db, "supplier", editing.id);
        await updateDoc(ref, payload);
      } else {
        // Creando nuevo proveedor - generar supplierID automáticamente
        
        // Obtener todos los proveedores existentes para calcular el próximo ID
        const snapshot = await getDocs(collection(db, "supplier"));
        const allSuppliers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Calcular el próximo supplierID disponible
        let candidateId = calcNextSupplierIdFromList(allSuppliers);
        
        // Verificación adicional para evitar duplicados (por si hay creaciones simultáneas)
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const dupQuery = query(collection(db, "supplier"), where("supplierId", "==", candidateId));
          const dupSnap = await getDocs(dupQuery);
          if (dupSnap.empty) break;
          candidateId = String(parseInt(candidateId, 10) + 1);
        }

        const payload = {
          ...form,
          supplierId: candidateId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        // Edificio que da de alta la ficha (C13: bid canónico, igual que
        // supplierCreate): el elegido en el filtro o, si administra uno solo,
        // ese. Las reglas 0D lo exigen (salvo al superadmin) y con él solo su
        // staff edita la ficha.
        const bidAlta =
          globalSelectedBuilding?.buildingid ?? (buildings?.length === 1 ? buildings[0].buildingid : null);
        if (bidAlta != null && String(bidAlta).trim() !== "") {
          payload.createdByBid = bidCanonico(bidAlta);
        }
        if (form.location) {
          payload.location = { lat: form.location.lat, lng: form.location.lng };
        }

        await addDoc(collection(db, "supplier"), payload);
        
        // Si hay un edificio seleccionado en el filtro global, asociar automáticamente
        if (globalSelectedBuilding?.buildingid) {
          await addDoc(collection(db, "supplierbuilding"), {
            supplierId: candidateId,
            buildingId: Number(globalSelectedBuilding.buildingid),
            showToClient: true,
            createdAt: serverTimestamp(),
          });
          setShowAutoAssociateNotice(true);
          setTimeout(() => setShowAutoAssociateNotice(false), 4000);
        }
      }

      setShowForm(false);
      setEditing(null);
      setForm({ name: "", type: "", email: "", telephone: "", address: "", razonSocial: "", rut: "", location: null });
      
      // Actualizar ambas listas: la de las cards Y la del modal de asociación
      await fetchProveedores();
      await fetchAllProviders();
    } catch (error) {
      console.error("Error guardando proveedor:", error);
      showToast(
        esPermisoDenegado(error)
          ? "No tenés permiso para guardar este proveedor. Elegí uno de tus edificios en el filtro y probá de nuevo."
          : "Error guardando proveedor.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (prov) => {
    setEditing(prov);
    setRutError(null);
    setForm({
      name: prov.name || "",
      type: prov.type || "",
      email: prov.email || "",
      telephone: prov.telephone || "",
      address: prov.address || "",
      razonSocial: prov.razonSocial || "",
      rut: prov.rut || "",
      location: prov.location ? { lat: prov.location.lat, lng: prov.location.lng } : null,
    });
    setShowForm(true);
  };

  const handleCrearProveedor = () => {
    setEditing(null);
    setRutError(null);
    setForm({ name: "", type: "", email: "", telephone: "", address: "", razonSocial: "", rut: "", location: null });
    setShowForm(true);
  };

  const fetchAllProviders = async () => {
    try {
      const snapshot = await getDocs(collection(db, "supplier"));
      const providers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllProviders(providers);
    } catch (error) {
      console.error("Error fetching all providers:", error);
      setAllProviders([]);
    }
  };

  const filterProviders = () => {
    let filtered = allProviders;
    
    if (searchTerm) {
      filtered = filtered.filter(provider => 
        provider.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredProviders(filtered);
  };

  const handleAsociarProveedor = () => {
    setSearchTerm("");
    setSelectedProvider(null);
    // Pre-seleccionar el edificio del filtro global si existe
    setSelectedBuilding(globalSelectedBuilding?.buildingid ? String(globalSelectedBuilding.buildingid) : "");
    setShowToClient(true);
    setShowProviderDropdown(false);
    setShowAssociateModal(true);
  };

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    setSearchTerm(provider.name);
    setShowProviderDropdown(false);
  };

  const handleAssociateProvider = async () => {
    if (!selectedProvider || !selectedBuilding) {
      showToast("Por favor selecciona un proveedor y un edificio", "warning");
      return;
    }

    setAssociating(true);
    try {
      // BUG-70 fix: Verificar duplicado antes de asociar
      const dupQ = query(
        collection(db, "supplierbuilding"),
        where("supplierId", "==", selectedProvider.supplierId),
        where("buildingId", "in", tolerantId(selectedBuilding))
      );
      const dupSnap = await getDocs(dupQ);
      if (!dupSnap.empty) {
        showToast("Este proveedor ya está asociado a este edificio.", "warning");
        setAssociating(false);
        return;
      }

      await addDoc(collection(db, "supplierbuilding"), {
        supplierId: selectedProvider.supplierId,
        buildingId: Number(selectedBuilding),
        showToClient: showToClient,
        createdAt: serverTimestamp(),
      });

      showToast("Proveedor asociado exitosamente al edificio", "success");
      setShowAssociateModal(false);
      fetchProveedores(); // Refresh the list
    } catch (error) {
      console.error("Error asociando proveedor:", error);
      showToast("Error al asociar el proveedor", "error");
    } finally {
      setAssociating(false);
    }
  };

  const handleLinkExistingSupplier = async () => {
    if (!duplicateRutSupplier || !globalSelectedBuilding?.buildingid) {
      setShowRutDuplicateModal(false);
      return;
    }

    try {
      const supplierId = String(duplicateRutSupplier.supplierId);
      const buildingId = Number(globalSelectedBuilding.buildingid);

      // Verificar si ya existe la asociación
      const assocQuery = query(
        collection(db, "supplierbuilding"),
        where("supplierId", "==", supplierId),
        where("buildingId", "in", tolerantId(buildingId))
      );
      const assocSnap = await getDocs(assocQuery);

      if (assocSnap.empty) {
        await addDoc(collection(db, "supplierbuilding"), {
          supplierId: supplierId,
          buildingId: buildingId,
          showToClient: true,
          createdAt: serverTimestamp(),
        });
        setShowAutoAssociateNotice(true);
        setTimeout(() => setShowAutoAssociateNotice(false), 4000);
      }

      setShowRutDuplicateModal(false);
      setDuplicateRutSupplier(null);
      setShowForm(false);
      setEditing(null);
      setForm({ name: "", type: "", email: "", telephone: "", address: "", razonSocial: "", rut: "", location: null });

      await fetchProveedores();
      await fetchAllProviders();
    } catch (error) {
      console.error("Error asociando proveedor existente:", error);
      showToast("Error al asociar el proveedor existente.", "error");
    }
  };

  return (
    <div style={styles.page}>
      {/* Header con título y acciones */}
      <div style={styles.pageHeader}>
        <ScreenHeader icon={FaPeopleCarry} title="Proveedores" subtitle="Gestión de proveedores" />
        <div style={styles.headerActions}>
          {/* Espaciador para empujar botones y filtros a la derecha */}
          <div style={{ flex: 1, minWidth: 12 }} />

          {/* Vista lista/mapa - al costado del botón Registrar */}
          <div style={styles.viewToggle}>
            <button
              style={{
                ...styles.viewBtn,
                ...(viewMode === "list" ? styles.viewBtnActive : {}),
              }}
              onClick={() => setViewMode("list")}
              title="Vista de lista"
            >
              <FaList size={14} />
            </button>
            <button
              style={{
                ...styles.viewBtn,
                ...(viewMode === "map" ? styles.viewBtnActive : {}),
              }}
              onClick={() => setViewMode("map")}
              title="Vista de mapa"
            >
              <FaMap size={14} />
            </button>
          </div>

          {/* Botones compactos */}
          <button
            style={{
              ...styles.iconActionBtn,
              ...styles.iconActionBtnPrimary,
              opacity: saving ? 0.7 : 1,
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
            onClick={handleCrearProveedor}
            disabled={saving}
            title="Registrar Proveedor"
          >
            <FaPeopleCarry size={16} />
          </button>
          <button
            style={{
              ...styles.iconActionBtn,
              ...styles.iconActionBtnSecondary,
              opacity: saving ? 0.7 : 1,
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
            onClick={handleAsociarProveedor}
            disabled={saving}
            title="Asociar Proveedor"
          >
            <FaLink size={16} />
          </button>

          {/* Filtros en esquina superior derecha */}
          {!loading && proveedores.length > 0 && (
            <div style={styles.filtersGroup}>
              <div style={styles.filterSelect}>
                <FaSearch size={12} color={colors.primary} />
                <input
                  type="text"
                  placeholder="RUT"
                  className="proveedores-filter-input"
                  style={styles.filterInput}
                  value={filterRut}
                  onChange={(e) => setFilterRut(e.target.value)}
                />
              </div>
              <div style={styles.filterSelect}>
                <FaSearch size={12} color={colors.primary} />
                <input
                  type="text"
                  placeholder="Nombre"
                  className="proveedores-filter-input"
                  style={styles.filterInput}
                  value={filterNombre}
                  onChange={(e) => setFilterNombre(e.target.value)}
                />
              </div>
              <div style={styles.filterSelect} ref={filterTipoRef}>
                <FaList size={12} color={colors.primary} />
                <div style={styles.tipoSelectWrapper}>
                  <button
                    type="button"
                    style={{
                      ...styles.filterTipoTrigger,
                      color: filterTipo ? colors.black : colors.gray400,
                    }}
                    onClick={() => setFilterTipoOpen(!filterTipoOpen)}
                  >
                    {filterTipo || "Tipo de Servicio"}
                  </button>
                  <FaChevronDown
                    size={10}
                    color={colors.primary}
                    style={{
                      ...styles.tipoSelectChevron,
                      transform: filterTipoOpen ? "translateY(-50%) rotate(180deg)" : "translateY(-50%)",
                    }}
                  />
                  {filterTipoOpen && (
                    <div style={styles.filterTipoDropdown}>
                      <button
                        type="button"
                        className="proveedores-filter-tipo-option"
                        style={{
                          ...styles.filterTipoOption,
                          ...(filterTipo === "" ? styles.filterTipoOptionActive : {}),
                        }}
                        onClick={() => {
                          setFilterTipo("");
                          setFilterTipoOpen(false);
                        }}
                      >
                        Tipo de Servicio
                      </button>
                      {SUPPLIER_TYPES.map((t) => (
                        <button
                          key={t}
                          type="button"
                          className="proveedores-filter-tipo-option"
                          style={{
                            ...styles.filterTipoOption,
                            ...(filterTipo === t ? styles.filterTipoOptionActive : {}),
                          }}
                          onClick={() => {
                            setFilterTipo(t);
                            setFilterTipoOpen(false);
                          }}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {(filterRut || filterNombre || filterTipo) && (
                <button
                  type="button"
                  className="proveedores-filter-clear-btn"
                  style={styles.filterClearIconBtn}
                  onClick={() => {
                    setFilterRut("");
                    setFilterNombre("");
                    setFilterTipo("");
                  }}
                  title="Limpiar filtros"
                >
                  <FaEraser size={14} color={colors.gray600} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <p>Cargando...</p>
      ) : proveedores.length === 0 ? (
        <p>{globalSelectedBuilding ? `No hay proveedores asociados a ${globalSelectedBuilding.name}.` : "No hay proveedores registrados."}</p>
      ) : filteredProveedores.length === 0 ? (
        <p style={{ color: colors.gray600, fontSize: typography.sizes.md }}>
          No hay proveedores que coincidan con los filtros aplicados.
        </p>
      ) : viewMode === "map" ? (
        /* Vista de Mapa */
        <div style={styles.mapContainer}>
          {loadError && <p style={styles.mapError}>Error al cargar Google Maps</p>}
          {!GOOGLE_MAPS_API_KEY && <p style={styles.mapError}>API Key de Google Maps no configurada</p>}
          {GOOGLE_MAPS_API_KEY && !isGoogleMapsLoaded && <p>Cargando mapa...</p>}
          {GOOGLE_MAPS_API_KEY && isGoogleMapsLoaded && (
            <>
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={getMapCenter()}
                zoom={proveedoresWithCoords.length > 0 ? 13 : 12}
                onLoad={onMapLoad}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: true,
                  zoomControl: true,
                }}
              >
                {proveedoresWithCoords.map((prov) => (
                  <Marker
                    key={prov.id}
                    position={{ lat: prov.location.lat, lng: prov.location.lng }}
                    onClick={() => setSelectedMarker(prov)}
                    icon={{
                      url: "data:image/svg+xml," + encodeURIComponent(`
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#0080ff" width="36" height="36">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                      `),
                      scaledSize: new window.google.maps.Size(36, 36),
                    }}
                  />
                ))}
                {selectedMarker && (
                  <InfoWindow
                    position={{ lat: selectedMarker.location.lat, lng: selectedMarker.location.lng }}
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <div style={styles.infoWindow}>
                      <h3 style={styles.infoTitle}>{selectedMarker.name}</h3>
                      <p style={styles.infoType}>{selectedMarker.type}</p>
                      <p style={styles.infoAddress}>
                        <FaMapMarkerAlt size={12} style={{ marginRight: 4 }} />
                        {selectedMarker.address}
                      </p>
                      {selectedMarker.telephone && (
                        <p style={styles.infoDetail}>📞 {selectedMarker.telephone}</p>
                      )}
                      {selectedMarker.email && (
                        <p style={styles.infoDetail}>✉️ {selectedMarker.email}</p>
                      )}
                      <button
                        style={styles.infoBtn}
                        onClick={() => {
                          handleEdit(selectedMarker);
                          setSelectedMarker(null);
                        }}
                      >
                        Editar proveedor
                      </button>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
              {proveedoresWithCoords.length === 0 && filteredProveedores.length > 0 && (
                <p style={styles.noCoords}>
                  Ningún proveedor tiene ubicación registrada. Las ubicaciones se guardan automáticamente al crear proveedores desde facturas.
                </p>
              )}
              {proveedoresWithCoords.length > 0 && (
                <p style={styles.mapLegend}>
                  Mostrando {proveedoresWithCoords.length} de {filteredProveedores.length} proveedores con ubicación registrada
                </p>
              )}
            </>
          )}
        </div>
      ) : (
        /* Vista de Lista */
        <div style={styles.grid}>
          {filteredProveedores.map((prov) => {
            // Buscar la relación con el edificio actual para saber si se muestra al cliente
            const relation = globalSelectedBuilding 
              ? supplierBuildingRelations.find(r => 
                  String(r.supplierId) === String(prov.supplierId) && 
                  Number(r.buildingId) === Number(globalSelectedBuilding.buildingid)
                )
              : null;
            const showsToClient = relation?.showToClient;
            
            return (
              <div key={prov.id} style={styles.card}>
                <button style={styles.editBtn} onClick={() => handleEdit(prov)} title="Editar proveedor">
                  <FaPen size={14} />
                </button>
                <h3 style={styles.cardTitle}>{prov.name}</h3>
                <div style={styles.cardDetails}>
                  {prov.type && (
                    <div style={styles.cardDetailRow}>
                      <FaTag size={14} color={colors.primary} style={styles.cardDetailIcon} />
                      <span style={styles.cardDetailText}>{prov.type}</span>
                    </div>
                  )}
                  {prov.razonSocial && (
                    <div style={styles.cardDetailRow}>
                      <FaBuilding size={14} color={colors.primary} style={styles.cardDetailIcon} />
                      <span style={styles.cardDetailText}>{prov.razonSocial}</span>
                    </div>
                  )}
                  {prov.rut && (
                    <div style={styles.cardDetailRow}>
                      <FaIdCard size={14} color={colors.primary} style={styles.cardDetailIcon} />
                      <span style={styles.cardDetailText}>{prov.rut}</span>
                    </div>
                  )}
                  {prov.email && (
                    <div style={styles.cardDetailRow}>
                      <FaEnvelope size={14} color={colors.primary} style={styles.cardDetailIcon} />
                      <span style={styles.cardDetailText}>{prov.email}</span>
                    </div>
                  )}
                  {prov.telephone && (
                    <div style={styles.cardDetailRow}>
                      <FaPhone size={14} color={colors.primary} style={styles.cardDetailIcon} />
                      <span style={styles.cardDetailText}>{prov.telephone}</span>
                    </div>
                  )}
                  {prov.address && (
                    <div style={styles.cardDetailRow}>
                      <FaMapMarkerAlt size={14} color={colors.primary} style={styles.cardDetailIcon} />
                      <span style={styles.cardDetailText}>{prov.address}</span>
                    </div>
                  )}
                </div>
                {prov.location?.lat && (
                  <p style={styles.cardLocation}>
                    <FaMapMarkerAlt size={12} color={colors.success} />
                    <span style={{ color: colors.success, fontSize: typography.sizes.xs }}>Ubicación registrada</span>
                  </p>
                )}
                {globalSelectedBuilding && (
                  <div style={styles.clientVisibility}>
                    <span style={showsToClient ? styles.visibleBadge : styles.hiddenBadge}>
                      {showsToClient ? "✓ Visible para residentes" : "✗ Oculto para residentes"}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Aviso de asociación automática */}
      {showAutoAssociateNotice && (
        <div style={styles.autoAssociateNotice}>
          <FaCheckCircle size={18} color={colors.success} />
          <span>Proveedor asociado automáticamente a <strong>{globalSelectedBuilding?.name}</strong></span>
        </div>
      )}

      {/* Modal Registrar/Editar Proveedor */}
      <AdminModal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? "Editar Proveedor" : "Registrar Proveedor"}
        icon={<FaPeopleCarry />}
        size="narrow"
        footer={
          <>
            <button
              onClick={() => setShowForm(false)}
              style={styles.btnCancel}
              className="habitia-btn-secondary"
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              style={{
                ...styles.btnSave,
                opacity: saving ? 0.7 : 1,
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
              className="habitia-btn-primary"
              disabled={saving}
            >
              {saving ? (editing ? "Guardando..." : "Registrando...") : (editing ? "Guardar" : "Registrar")}
            </button>
          </>
        }
      >
            {/* Aviso de asociacion automatica */}
            {!editing && globalSelectedBuilding && (
              <div style={styles.autoAssociateInfo}>
                <FaCheckCircle size={16} color={colors.primary} />
                <span>Se asociara automaticamente a <strong>{globalSelectedBuilding.name}</strong></span>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: spacing[3] }}>
              <input
                placeholder="Nombre"
                style={styles.input}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <div style={styles.formGroup}>
                <label style={{ ...styles.label, fontSize: typography.sizes.sm }}>Tipo</label>
                <div style={{ ...styles.tipoSelectWrapper, width: "100%" }}>
                  <select
                    style={{
                      ...styles.input,
                      width: "100%",
                      appearance: "none",
                      WebkitAppearance: "none",
                      paddingRight: 32,
                      cursor: "pointer",
                    }}
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  >
                    <option value="">Selecciona un tipo</option>
                    {SUPPLIER_TYPES.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <FaChevronDown size={12} color={colors.primary} style={styles.tipoSelectChevron} />
                </div>
              </div>
              <input
                placeholder="Email"
                style={styles.input}
                type="email"
                inputMode="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <input
                placeholder="Telefono"
                style={styles.input}
                type="tel"
                inputMode="tel"
                value={form.telephone}
                onChange={(e) => setForm({ ...form, telephone: e.target.value })}
              />
              <div style={styles.formGroup}>
                <label style={{ ...styles.label, fontSize: typography.sizes.sm }}>Direccion {form.location && <span style={{ color: colors.success, fontWeight: 400 }}>(ubicacion registrada)</span>}</label>
                <input
                  ref={supplierAddressInputRef}
                  placeholder="Escribi para buscar direccion..."
                  style={{
                    ...styles.input,
                    ...(form.location ? { borderColor: colors.success, backgroundColor: colors.successLight } : {})
                  }}
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value, location: null })}
                />
                {form.location && (
                  <a
                    href={`https://www.google.com/maps?q=${form.location.lat},${form.location.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: typography.sizes.xs, color: colors.primary, marginTop: spacing[1], display: 'inline-block' }}
                  >
                    Ver en mapa
                  </a>
                )}
              </div>
              <input
                placeholder="Razon Social"
                style={styles.input}
                value={form.razonSocial}
                onChange={(e) => setForm({ ...form, razonSocial: e.target.value })}
              />
              <div style={styles.formGroup}>
                <label style={{ ...styles.label, fontSize: typography.sizes.sm }}>RUT</label>
                <input
                  placeholder="12 digitos sin puntos ni guiones"
                  style={{
                    ...styles.input,
                    borderColor: rutError ? colors.errorDark : (form.rut && validateRutUY(form.rut).valid ? colors.success : undefined)
                  }}
                  value={form.rut}
                  onChange={(e) => {
                    setForm({ ...form, rut: e.target.value });
                    setRutError(null);
                  }}
                  onBlur={() => {
                    if (form.rut) {
                      const v = validateRutUY(form.rut);
                      setRutError(v.valid ? null : v.message);
                    }
                  }}
                />
                {rutError && (
                  <span style={{ fontSize: typography.sizes.sm, color: colors.errorDark, marginTop: spacing[1] }}>{rutError}</span>
                )}
                {form.rut && !rutError && validateRutUY(form.rut).valid && (
                  <span style={{ fontSize: typography.sizes.sm, color: colors.success, marginTop: spacing[1], fontWeight: 400 }}>RUT validado</span>
                )}
              </div>
            </div>
      </AdminModal>

      {/* Modal Asociar Proveedor */}
      <AdminModal
        open={showAssociateModal}
        onClose={() => setShowAssociateModal(false)}
        title="Asociar Proveedor"
        icon={<FaLink />}
        size="medium"
        footer={
          <>
            <button
              onClick={() => setShowAssociateModal(false)}
              style={styles.btnCancel}
              className="habitia-btn-secondary"
            >
              Cancelar
            </button>
            <button
              onClick={handleAssociateProvider}
              style={styles.btnSave}
              className="habitia-btn-primary"
              disabled={associating || !selectedProvider || !selectedBuilding}
            >
              {associating ? "Asociando..." : "Asociar"}
            </button>
          </>
        }
      >
            {/* Seleccion de proveedor */}
            <div style={styles.providerSelection}>
              <h3 style={styles.sectionTitle}>Seleccionar Proveedor</h3>
              <div style={styles.searchableDropdown} data-dropdown>
                <div style={styles.searchContainer}>
                  <FaSearch size={18} style={{color: colors.gray600}} />
                  <input
                    placeholder="Buscar y seleccionar proveedor..."
                    style={styles.searchInput}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowProviderDropdown(true);
                      if (!e.target.value) {
                        setSelectedProvider(null);
                      }
                    }}
                    onFocus={() => setShowProviderDropdown(true)}
                  />
                </div>

                {showProviderDropdown && (
                  <div style={styles.dropdownList}>
                    {filteredProviders.length === 0 ? (
                      <div style={styles.dropdownItem}>
                        {searchTerm ? "No se encontraron proveedores" : "No hay proveedores"}
                      </div>
                    ) : (
                      filteredProviders.map(provider => (
                        <div
                          key={provider.id}
                          style={styles.dropdownItem}
                          onClick={() => handleProviderSelect(provider)}
                          onMouseEnter={(e) => e.target.style.backgroundColor = colors.gray50}
                          onMouseLeave={(e) => e.target.style.backgroundColor = colors.white}
                        >
                          <div style={styles.providerName}>{provider.name}</div>
                          <div style={styles.providerType}>{provider.type}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Seleccion de edificio */}
            <div style={styles.buildingSelection}>
              <h3 style={styles.sectionTitle}>Seleccionar Edificio</h3>
              <select
                style={styles.buildingSelect}
                value={selectedBuilding}
                onChange={(e) => setSelectedBuilding(e.target.value)}
              >
                <option value="">Selecciona un edificio</option>
                {buildings?.map(building => (
                  <option key={building.buildingid} value={building.buildingid}>
                    {building.name} - {building.city}
                  </option>
                ))}
              </select>
            </div>

            {/* Opcion mostrar al cliente final */}
            <div style={styles.showToClientOption}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={showToClient}
                  onChange={(e) => setShowToClient(e.target.checked)}
                  style={styles.checkbox}
                />
                <span>Mostrar al cliente final</span>
              </label>
            </div>
      </AdminModal>

      {/* Modal RUT Duplicado */}
      <AdminModal
        open={showRutDuplicateModal && !!duplicateRutSupplier}
        onClose={() => {
          setShowRutDuplicateModal(false);
          setDuplicateRutSupplier(null);
        }}
        title="Proveedor con RUT existente"
        icon={<FaIdCard />}
        size="medium"
        footer={
          <>
            <button
              onClick={() => {
                setShowRutDuplicateModal(false);
                setDuplicateRutSupplier(null);
              }}
              style={styles.btnCancel}
              className="habitia-btn-secondary"
            >
              Cancelar
            </button>
            {globalSelectedBuilding && (
              <button
                onClick={handleLinkExistingSupplier}
                style={styles.btnSave}
                className="habitia-btn-primary"
              >
                Asociar al edificio
              </button>
            )}
          </>
        }
      >
            {duplicateRutSupplier && (
              <>
                <div style={styles.rutDuplicateWarning}>
                  <FaIdCard size={20} color={colors.warning} />
                  <span>Ya existe un proveedor con el RUT <strong>{form.rut}</strong></span>
                </div>

                <div style={{ ...styles.existingSupplierCard, marginTop: spacing[4] }}>
                  <h4 style={{ margin: "0 0 10px", fontSize: typography.sizes.md, fontWeight: 400, color: colors.black }}>
                    Proveedor existente
                  </h4>
                  <div style={{ display: "flex", flexDirection: "column", gap: spacing[2] }}>
                    {duplicateRutSupplier.name && (
                      <div style={styles.existingSupplierRow}>
                        <FaPeopleCarry size={13} color={colors.primary} />
                        <span style={styles.existingSupplierText}>{duplicateRutSupplier.name}</span>
                      </div>
                    )}
                    {duplicateRutSupplier.type && (
                      <div style={styles.existingSupplierRow}>
                        <FaTag size={13} color={colors.primary} />
                        <span style={styles.existingSupplierText}>{duplicateRutSupplier.type}</span>
                      </div>
                    )}
                    {duplicateRutSupplier.email && (
                      <div style={styles.existingSupplierRow}>
                        <FaEnvelope size={13} color={colors.primary} />
                        <span style={styles.existingSupplierText}>{duplicateRutSupplier.email}</span>
                      </div>
                    )}
                    {duplicateRutSupplier.telephone && (
                      <div style={styles.existingSupplierRow}>
                        <FaPhone size={13} color={colors.primary} />
                        <span style={styles.existingSupplierText}>{duplicateRutSupplier.telephone}</span>
                      </div>
                    )}
                    {duplicateRutSupplier.rut && (
                      <div style={styles.existingSupplierRow}>
                        <FaIdCard size={13} color={colors.primary} />
                        <span style={styles.existingSupplierText}>{duplicateRutSupplier.rut}</span>
                      </div>
                    )}
                  </div>
                </div>

                {globalSelectedBuilding && (
                  <p style={{ fontSize: typography.sizes.base, color: colors.gray700, margin: `${spacing[3]}px 0 0` }}>
                    Se asociara este proveedor a <strong>{globalSelectedBuilding.name}</strong>.
                  </p>
                )}
              </>
            )}
      </AdminModal>
    </div>
  );
};

// Estilos migrados a design tokens
const styles = {
  // Contenedor principal de la pagina
  page: { ...pageContainer, position: "relative", overflowX: "hidden" },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing[3],
  },
  headerTitleSection: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  // Titulo de pagina (usa token importado pageTitle)
  pageTitle: { ...pageTitle },
  pageSubtitle: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: 0,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
    flexWrap: "wrap",
    flex: 1,
    minWidth: 0,
  },
  // Grupo de filtros
  filtersGroup: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    flexShrink: 1,
    minWidth: 0,
  },
  filterSelect: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    background: colors.gray50,
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
    padding: `${spacing[2]}px ${spacing[3]}px`,
    position: "relative",
    flexShrink: 1,
    minWidth: 0,
  },
  filterTipoTrigger: {
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: typography.sizes.base,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: typography.fontFamily,
    minWidth: 160,
    maxWidth: 200,
    textAlign: "left",
    padding: 0,
    paddingRight: spacing[5],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  // Dropdown de filtro tipo
  filterTipoDropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: spacing[1],
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.md,
    border: `1px solid ${colors.gray200}`,
    overflow: "hidden",
    zIndex: 100,
    minWidth: 240,
    maxHeight: 280,
    overflowY: "auto",
  },
  filterTipoOption: {
    display: "block",
    width: "100%",
    padding: `${spacing[3]}px ${spacing[4]}px`,
    border: "none",
    background: "transparent",
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.black,
    cursor: "pointer",
    textAlign: "left",
    fontFamily: typography.fontFamily,
    transition: `background ${transitions.fast}, color ${transitions.fast}`,
  },
  filterTipoOptionActive: {
    background: colors.primaryLighter,
    color: colors.primary,
  },
  filterInput: {
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: typography.sizes.base,
    width: 110,
    minWidth: 70,
    fontWeight: 400,
    color: colors.black,
    fontFamily: typography.fontFamily,
  },
  filterSelectInput: {
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.black,
    cursor: "pointer",
    fontFamily: typography.fontFamily,
    width: 120,
    minWidth: 120,
    maxWidth: 180,
    appearance: "none",
    WebkitAppearance: "none",
    paddingRight: 18,
  },
  tipoSelectWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  tipoSelectChevron: {
    position: "absolute",
    right: spacing[2],
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
  },
  // Boton limpiar filtros
  filterClearIconBtn: {
    ...btnGhost,
    width: 36,
    height: 36,
    border: `1px solid ${colors.gray200}`,
    background: colors.gray50,
    flexShrink: 0,
  },
  // Botones de accion (iconos)
  iconActionBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: radius.sm,
    border: "none",
    cursor: "pointer",
    transition: `all ${transitions.fast}`,
  },
  iconActionBtnPrimary: {
    background: colors.primary,
    color: colors.white,
    boxShadow: shadows.sm,
  },
  iconActionBtnSecondary: {
    background: colors.white,
    color: colors.primary,
    border: `1px solid ${colors.primary}`,
  },
  // Boton agregar proveedor
  addBtn: {
    ...btnPrimary,
    boxShadow: shadows.sm,
  },
  // Boton secundario
  secondaryBtn: {
    ...btnSecondary,
  },
  // Boton volver
  backBtn: {
    ...btnSecondary,
    gap: spacing[2],
    padding: `${spacing[2]}px ${spacing[4]}px`,
  },
  title: { fontSize: typography.sizes.xl, fontWeight: 400, color: colors.primary, margin: 0, display: "flex", alignItems: "center" },
  // Grilla de cards de proveedores
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: spacing[4],
  },
  // Card de proveedor - sin borde, solo sombra
  card: {
    ...cardBase,
    padding: spacing[4],
    position: "relative",
  },
  cardTitle: { margin: "0 0 10px", fontSize: typography.sizes.xl, fontWeight: 400, color: colors.black },
  cardDetails: { display: "flex", flexDirection: "column", gap: spacing[2] },
  cardDetailRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minHeight: 0,
  },
  cardDetailIcon: { flexShrink: 0, width: 16 },
  cardDetailText: { fontSize: typography.sizes.md, color: colors.gray700, lineHeight: 1.4 },
  // Visibilidad para residentes
  clientVisibility: {
    marginTop: spacing[3],
    paddingTop: spacing[3],
    borderTop: `1px solid ${colors.gray200}`,
  },
  visibleBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: `${spacing[1]}px ${spacing[3]}px`,
    background: colors.successLight,
    color: colors.success,
    borderRadius: radius.sm,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
  },
  hiddenBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: `${spacing[1]}px ${spacing[3]}px`,
    background: colors.gray100,
    color: colors.gray600,
    borderRadius: radius.sm,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
  },
  // Boton editar (esquina superior derecha de la card)
  editBtn: {
    ...btnGhost,
    position: "absolute",
    top: spacing[2],
    right: spacing[2],
    background: colors.gray100,
    borderRadius: radius.sm,
    padding: spacing[2],
  },
  // Aviso flotante de asociacion automatica
  autoAssociateNotice: {
    position: "fixed",
    bottom: spacing[6],
    right: spacing[6],
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
    padding: `${spacing[4]}px ${spacing[5]}px`,
    background: colors.white,
    border: `1px solid ${colors.successLight}`,
    borderRadius: radius.md,
    boxShadow: shadows.md,
    fontSize: typography.sizes.md,
    color: colors.black,
    zIndex: 1000,
  },
  // Info de asociacion automatica en modal
  autoAssociateInfo: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
    padding: `${spacing[3]}px ${spacing[4]}px`,
    background: colors.primaryLighter,
    border: `1px solid ${colors.primaryLight}`,
    borderRadius: radius.sm,
    fontSize: typography.sizes.base,
    color: colors.black,
  },
  // Input de formulario
  input: {
    ...inputBase,
    backgroundColor: colors.gray50,
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[1],
  },
  label: {
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    color: colors.gray900,
  },
  // Boton cancelar en modales
  btnCancel: {
    ...btnSecondary,
    padding: `${spacing[2]}px ${spacing[4]}px`,
  },
  // Boton guardar/registrar en modales
  btnSave: {
    ...btnPrimary,
    padding: `${spacing[2]}px ${spacing[4]}px`,
  },
  // Seccion seleccion de proveedor en modal asociar
  providerSelection: {
    marginBottom: spacing[5],
  },
  sectionTitle: {
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    margin: `0 0 ${spacing[3]}px`,
    color: colors.gray900,
  },
  searchableDropdown: {
    position: "relative",
    width: "100%",
  },
  // Contenedor de busqueda
  searchContainer: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    padding: `${spacing[3]}px ${spacing[3]}px`,
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
    background: colors.white,
  },
  searchInput: {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: typography.sizes.md,
    fontFamily: typography.fontFamily,
  },
  // Lista desplegable de proveedores
  dropdownList: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    background: colors.white,
    border: `1px solid ${colors.gray200}`,
    borderTop: "none",
    borderBottomLeftRadius: radius.sm,
    borderBottomRightRadius: radius.sm,
    maxHeight: 200,
    overflow: "auto",
    zIndex: 1000,
    boxShadow: shadows.md,
  },
  dropdownItem: {
    padding: spacing[3],
    cursor: "pointer",
    borderBottom: `1px solid ${colors.gray100}`,
    transition: `background-color ${transitions.fast}`,
  },
  providerName: {
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.black,
    marginBottom: 2,
  },
  providerType: {
    fontSize: typography.sizes.sm,
    color: colors.gray600,
  },
  // Seccion seleccion de edificio en modal asociar
  buildingSelection: {
    marginBottom: spacing[5],
  },
  buildingSelect: {
    ...inputBase,
    maxWidth: 320,
  },
  // Opcion mostrar al cliente
  showToClientOption: {
    marginBottom: spacing[5],
    padding: spacing[3],
    backgroundColor: colors.gray50,
    borderRadius: radius.sm,
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    cursor: "pointer",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.gray900,
  },
  checkbox: {
    width: 18,
    height: 18,
    cursor: "pointer",
  },
  // Estilos para vista de mapa
  viewToggle: {
    display: "flex",
    gap: spacing[1],
    background: colors.gray100,
    borderRadius: radius.sm,
    padding: spacing[1],
    border: `1px solid ${colors.gray200}`,
  },
  viewBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 32,
    background: "transparent",
    border: "none",
    borderRadius: radius.sm,
    cursor: "pointer",
    color: colors.gray600,
    transition: `all ${transitions.fast}`,
  },
  viewBtnActive: {
    background: colors.primary,
    color: colors.white,
    boxShadow: shadows.sm,
  },
  // Contenedor del mapa
  mapContainer: {
    ...cardBase,
    padding: spacing[4],
  },
  mapError: {
    padding: spacing[5],
    textAlign: "center",
    color: colors.errorDark,
    background: colors.errorLight,
    borderRadius: radius.sm,
  },
  noCoords: {
    marginTop: spacing[3],
    padding: spacing[3],
    textAlign: "center",
    color: colors.warningDark,
    background: colors.warningLight,
    borderRadius: radius.sm,
    fontSize: typography.sizes.base,
  },
  mapLegend: {
    marginTop: spacing[3],
    padding: spacing[2],
    textAlign: "center",
    color: colors.gray600,
    fontSize: typography.sizes.sm,
  },
  // Ventana de info del marcador en mapa
  infoWindow: {
    padding: spacing[1],
    minWidth: 180,
    maxWidth: 250,
  },
  infoTitle: {
    margin: "0 0 4px 0",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.black,
  },
  infoType: {
    margin: `0 0 ${spacing[2]}px 0`,
    fontSize: typography.sizes.sm,
    color: colors.primary,
    fontWeight: 400,
  },
  infoAddress: {
    margin: "0 0 4px 0",
    fontSize: typography.sizes.sm,
    color: colors.gray700,
    display: "flex",
    alignItems: "center",
  },
  infoDetail: {
    margin: "0 0 4px 0",
    fontSize: typography.sizes.xs,
    color: colors.gray600,
  },
  // Boton editar en ventana de info del mapa
  infoBtn: {
    ...btnPrimary,
    marginTop: spacing[2],
    width: "100%",
    padding: `${spacing[2]}px ${spacing[3]}px`,
    fontSize: typography.sizes.sm,
    justifyContent: "center",
  },
  // Ubicacion registrada en la card
  cardLocation: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    marginTop: spacing[3],
    padding: `${spacing[2]}px 0`,
    borderTop: `1px dashed ${colors.gray200}`,
  },
  // Estilos para modal de RUT duplicado
  rutDuplicateWarning: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
    padding: `${spacing[3]}px ${spacing[4]}px`,
    background: colors.warningLight,
    border: `1px solid ${colors.warning}`,
    borderRadius: radius.sm,
    fontSize: typography.sizes.base,
    color: colors.warningDark,
  },
  existingSupplierCard: {
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.md,
    padding: `${spacing[4]}px ${spacing[4]}px`,
    background: colors.gray50,
  },
  existingSupplierRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
  },
  existingSupplierText: {
    fontSize: typography.sizes.base,
    color: colors.gray700,
  },
};

export default ProveedoresEdificio;
